import { assertNever } from '@kofno/piper';
import { action, computed, makeObservable, observable } from 'mobx';
import { ParticipantsResource } from '../../../../ParticipantsStore/Types';
import { TPlainTextKey } from '../../../../Translations';
import { failed, initialQuery, loading, nextSort, Query, ready, SortColumn, State } from './Types';

class ParticipantsV2Store {
  @observable
  state: State = loading(initialQuery);

  /** What is in the search box. The query follows it after a debounce. */
  @observable
  searchText: string = '';

  constructor() {
    makeObservable(this);
  }

  @computed
  get query(): Query {
    switch (this.state.kind) {
      case 'loading':
      case 'ready':
      case 'error':
        return this.state.query;
      default:
        return assertNever(this.state);
    }
  }

  /** Responses for a query the admin has since moved past are dropped. */
  @action
  ready = (query: Query, resource: ParticipantsResource) => {
    switch (this.state.kind) {
      case 'loading':
        this.state = this.state.query === query ? ready(query, resource) : this.state;
        break;
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  failed = (query: Query, message: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'loading':
        this.state = this.state.query === query ? failed(query, message) : this.state;
        break;
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  typed = (text: string) => {
    this.searchText = text;
  };

  /** A new query text always starts again from page 1. */
  @action
  search = (text: string) => {
    switch (this.state.kind) {
      case 'loading':
      case 'ready':
      case 'error':
        this.state =
          this.state.query.text === text
            ? this.state
            : loading({ ...this.state.query, text, page: 1 });
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  clearQuery = () => {
    this.searchText = '';
    this.search('');
  };

  /** A new sort also starts again from page 1. */
  @action
  sortBy = (column: SortColumn) => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = loading({
          ...this.state.query,
          sort: nextSort(this.state.query.sort, column),
          page: 1,
        });
        break;
      case 'loading':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  goToPage = (page: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = loading({ ...this.state.query, page });
        break;
      case 'loading':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };
}

export default ParticipantsV2Store;
